import Button from 'components/Button';
import FormHeader from 'components/Form/FormHeader';
import FormResult from 'components/Form/FormResult';
import Link from 'components/Link';
import NavigationTabs from 'components/Navigation/NavigationTabs';
import TooltipWrapper from 'components/Tooltip';
import useBeforeUnload from 'hooks/useBeforeUnload';
import {setMessage} from 'hooks/useMessage';
import lodash from 'lodash';
import {FC} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useBackend} from 'state/backend';
import {jt, t} from 'ttag';
import {setUrl} from 'utils/request';
import {onApiError} from 'utils/request-axios/utils';

import {
    PdfFilenameSettingsFormFields,
    usePutPdfFilenameSettingsEdit,
} from '../services';
import {PdfFilenameSettingsBackendData} from '../types';
import Section from './Section';

const EditPdfFilenameSettingsPage: FC = () => {
    const {data} = useBackend<PdfFilenameSettingsBackendData>();

    const methods = useForm<PdfFilenameSettingsFormFields>({
        defaultValues: {
            pdfFilenameClient: data.pdfFilenameClient,
            pdfFilenameEmployee: data.pdfFilenameEmployee,
        },
    });

    const {isPending, mutateAsync} = usePutPdfFilenameSettingsEdit();

    const {pdfFilenameClient, pdfFilenameEmployee} = methods.watch();

    const isValid =
        pdfFilenameClient.length > 0 && pdfFilenameEmployee.length > 0;

    const {allowPageChange} = useBeforeUnload(
        !lodash.isEqual(
            {pdfFilenameClient, pdfFilenameEmployee},
            {
                pdfFilenameClient: data.pdfFilenameClient,
                pdfFilenameEmployee: data.pdfFilenameEmployee,
            },
        ),
    );

    const onSubmit = () => {
        mutateAsync({mid: data.mid, pdfFilenameClient, pdfFilenameEmployee})
            .then(() => {
                allowPageChange();
                setMessage(
                    'successMessage',
                    t`PDF filename settings have been saved.`,
                );
                setUrl(window.Urls.pdf_filename_settings_view());
            })
            .catch(onApiError(methods));
    };

    const link = (
        <Link
            href="https://help.makeleaps.jp/ja/articles/8059992"
            isOpenInNewTab={true}
            key="subtitle-link"
        >{t`See details`}</Link>
    );
    const subTitle = jt`Select and choose the order of the components for Document PDF filenames. ${link}`;

    return (
        <div>
            <FormProvider {...methods}>
                <FormResult className="mb-4" />
                <NavigationTabs.DocumentSettings selection="pdfFilenameSettings" />
                <FormHeader
                    button={{
                        disabled: !isValid,
                        isLoading: isPending,
                        kind: 'primary',
                        label: t`Save`,
                        onClick: onSubmit,
                        type: 'submit',
                    }}
                    subtitle={subTitle}
                    title={t`PDF Filename`}
                />
                <Section sectionName="pdfFilenameEmployee" />
                <Section sectionName="pdfFilenameClient" />
                <TooltipWrapper
                    message={
                        isValid
                            ? ''
                            : t`At least one component must be selected.`
                    }
                >
                    <Button
                        disabled={!isValid}
                        isLoading={isPending}
                        kind="primary"
                        onClick={onSubmit}
                        size="sm"
                    >
                        {t`Save`}
                    </Button>
                </TooltipWrapper>
            </FormProvider>
        </div>
    );
};

export default EditPdfFilenameSettingsPage;
